/**
 * <header-mega>
 *
 * Hosts the Cartier-style mega menu. Owns hover intent, the dimming
 * overlay, and closing on Escape / outside-click / scroll. Desktop-only
 * hover behavior is gated behind a hover+fine-pointer media query so
 * touch and keyboard users always get native <details>/<summary>
 * tap-to-open behavior instead.
 *
 * Architectural rule: this element never tracks its own open/closed
 * state. It derives everything from a single 'toggle' event listener
 * (capture phase, since 'toggle' doesn't bubble). That means any
 * existing code that closes a <details> disclosure — Escape handling,
 * focus-out in details-disclosure.js, StickyHeader.closeMenuDisclosure()
 * — automatically drives the overlay too, with zero coordination code.
 */
if (!customElements.get('header-mega')) {
  customElements.define(
    'header-mega',
    class HeaderMega extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.header-mega__item'));
        this.detailsEls = Array.from(this.querySelectorAll('details.header-mega__details'));
        this.hoverQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
        this.openTimer = null;
        this.closeTimer = null;

        this.onToggle = this.onToggle.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onLeave = this.onLeave.bind(this);

        this.addEventListener('toggle', this.onToggle, true);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('pointerleave', this.onLeave);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.items.forEach((item) => {
          const summary = item.querySelector('summary.header-mega__trigger');
          const details = item.querySelector('details.header-mega__details');
          if (!summary || !details) return;

          item.addEventListener('pointerenter', () => this.scheduleOpen(details));
          summary.addEventListener('click', (event) => {
            if (this.hoverQuery.matches) {
              // In hover mode the panel is likely already open by the time a
              // click lands — don't let the native toggle close it again.
              event.preventDefault();
              this.openOnly(details);
            }
          });
        });

        this.lastScrollY = window.scrollY;
      }

      disconnectedCallback() {
        this.removeEventListener('toggle', this.onToggle, true);
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('pointerleave', this.onLeave);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('scroll', this.onScroll);
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
      }

      onToggle(event) {
        const anyOpen = this.detailsEls.some((d) => d.open);
        this.classList.toggle('header-mega--open', anyOpen);
        this.detailsEls.forEach((details) => {
          const summary = details.querySelector('summary.header-mega__trigger');
          summary?.setAttribute('aria-expanded', String(details.open));
        });

        const target = event.target;
        if (target?.open === false) {
          target.querySelector('mega-tabs')?.reset();
        }
      }

      scheduleOpen(details) {
        if (!this.hoverQuery.matches) return;
        clearTimeout(this.closeTimer);
        this.openTimer = setTimeout(() => this.openOnly(details), 90);
      }

      openOnly(details) {
        this.detailsEls.forEach((d) => {
          if (d !== details) d.open = false;
        });
        details.open = true;
      }

      closeAll() {
        this.detailsEls.forEach((d) => (d.open = false));
      }

      onLeave() {
        if (!this.hoverQuery.matches) return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.closeAll(), 220);
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          const openDetails = this.detailsEls.find((d) => d.open);
          if (!openDetails) return;
          openDetails.open = false;
          openDetails.querySelector('summary.header-mega__trigger')?.focus();
        }
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.closeAll();
      }

      onScroll() {
        const y = window.scrollY;
        if (Math.abs(y - this.lastScrollY) > 40) {
          this.closeAll();
        }
        this.lastScrollY = y;
      }
    }
  );
}

/**
 * <mega-tabs>
 *
 * Owns tab activation only — nothing about opening/closing the panel.
 * Implements the WAI-ARIA tabs "automatic activation" pattern: hover,
 * focus, and click all call the same activate(index), so hover-swap
 * and keyboard-swap are the same code path.
 */
if (!customElements.get('mega-tabs')) {
  customElements.define(
    'mega-tabs',
    class MegaTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        if (this.current < 0) this.current = 0;
        this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('pointerenter', () => {
            if (this.hoverQuery.matches) this.activate(index);
          });
          tab.addEventListener('focus', () => this.activate(index));
          tab.addEventListener('click', () => this.activate(index));
        });

        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      activate(index) {
        if (index === this.current) return;
        const total = this.tabs.length;
        const next = ((index % total) + total) % total;

        this.tabs[this.current]?.setAttribute('aria-selected', 'false');
        this.tabs[this.current]?.setAttribute('tabindex', '-1');
        this.panels[this.current]?.setAttribute('hidden', '');

        this.current = next;

        this.tabs[this.current]?.setAttribute('aria-selected', 'true');
        this.tabs[this.current]?.setAttribute('tabindex', '0');
        this.panels[this.current]?.removeAttribute('hidden');
      }

      reset() {
        if (this.current === 0) return;
        this.activate(0);
      }

      onKeydown(event) {
        const total = this.tabs.length;
        if (total === 0) return;

        let targetIndex = null;
        if (event.key === 'ArrowRight') targetIndex = (this.current + 1) % total;
        else if (event.key === 'ArrowLeft') targetIndex = (this.current - 1 + total) % total;
        else if (event.key === 'Home') targetIndex = 0;
        else if (event.key === 'End') targetIndex = total - 1;
        else return;

        event.preventDefault();
        this.activate(targetIndex);
        this.tabs[targetIndex]?.focus();
      }
    }
  );
}
